// Cart-Forth syntax mode for CodeMirror 6.
//
// Forth grammar is small: tokens are whitespace-delimited "words". We
// classify each by checking the keyword/builtin tables and falling back
// to numeric-literal / user-name detection. CodeMirror's StreamLanguage
// adapter is the simplest fit — no Lezer grammar needed.

import { StreamLanguage, HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

// Cart-Forth control + defining words. Compile-time vocabulary.
const KEYWORDS = new Set([
    ':', ';',
    'if', 'else', 'then',
    'begin', 'until', 'while', 'repeat', 'again',
    'do', 'loop', '+loop', 'leave', 'i', 'j',
    'variable', 'constant',
    'recurse', 'exit',
]);

// Built-in words exposed by the cart runtime — see crates/lang/src/compiler.rs
// for the authoritative list. Keep this in sync.
const BUILTINS = new Set([
    // display HCALLs
    'cls', 'pixel', 'rect', 'rect-fill', 'sprite', 'line', 'camera', 'clip', 'print',
    // audio HCALLs
    'note!', 'mute!',
    // input HCALLs
    'btn', 'btnp',
    // system (P3+)
    'time', 'random', 'seed!', 'cart-hash',
    // stack primitives
    'dup', 'drop', 'swap', 'over', 'rot', '-rot', 'nip', 'tuck', 'pick', '2dup', '2drop',
    '>r', 'r>', 'r@',
    // arithmetic primitives
    '+', '-', '*', '/', 'mod', '/mod', 'negate', 'abs', 'min', 'max',
    // comparison primitives
    '=', '<>', '<', '>', '<=', '>=', '0=', '0<', '0>',
    // bitwise primitives
    'and', 'or', 'xor', 'invert', 'lshift', 'rshift',
    // memory primitives
    '@', '!', 'c@', 'c!', '+!',
]);

const forthLanguage = StreamLanguage.define({
    name: 'forth',
    startState: () => ({}),
    token(stream, _state) {
        if (stream.eatSpace()) return null;

        // Line comment: `\` followed by whitespace (or eol).
        if (stream.match(/^\\(?:\s|$)/)) {
            stream.skipToEnd();
            return 'comment';
        }

        // Block / stack-effect comment: `( ... )` — only when `(` is followed
        // by whitespace, matching standard Forth (avoids misclassifying `(` in
        // word names, e.g. `(foo)` is a single token, not a comment open).
        if (stream.match(/^\(\s/)) {
            while (!stream.eol()) {
                if (stream.match(/\)/)) return 'comment';
                stream.next();
            }
            return 'comment';
        }

        // String literal: `." text"` (Forth's print-string defining form).
        if (stream.match(/^\.\"/)) {
            while (!stream.eol()) {
                const ch = stream.next();
                if (ch === '"') return 'string';
            }
            return 'string';
        }

        // Numeric literal — decimal or hex. Match before generic word so
        // tokens like `-1` / `0x42` highlight correctly.
        if (stream.match(/^[+-]?(?:0x[0-9a-fA-F]+|[0-9]+)(?=\s|$)/)) return 'number';

        // Generic whitespace-delimited word.
        const tokenMatch = stream.match(/^\S+/);
        if (tokenMatch) {
            // CodeMirror's StringStream.match returns either a RegExpMatchArray
            // or, depending on the regex, the matched string directly.
            const tok = typeof tokenMatch === 'string' ? tokenMatch : tokenMatch[0];
            if (typeof tok !== 'string') return null;
            if (KEYWORDS.has(tok)) return 'keyword';
            // 'builtin' is StreamLanguage's legacy alias for
            // `variableName.standard` (see @codemirror/language's
            // defaultTable). Maps to `t.standard(t.variableName)` below.
            if (BUILTINS.has(tok)) return 'builtin';
            return 'variableName';
        }

        // Defensive fallback — should never trigger thanks to eatSpace above.
        stream.next();
        return null;
    },
    languageData: {
        commentTokens: { line: '\\' },
    },
});

const forthHighlight = HighlightStyle.define([
    { tag: t.keyword, color: 'var(--cm-keyword, #c678dd)', fontWeight: 'bold' },
    { tag: t.standard(t.variableName), color: 'var(--cm-builtin, #61afef)' },
    { tag: t.variableName, color: 'var(--cm-name, #abb2bf)' },
    { tag: t.number, color: 'var(--cm-number, #d19a66)' },
    { tag: t.string, color: 'var(--cm-string, #98c379)' },
    { tag: t.comment, color: 'var(--cm-comment, #5c6370)', fontStyle: 'italic' },
]);

export function forth() {
    return [
        forthLanguage,
        syntaxHighlighting(forthHighlight),
    ];
}
